import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { carrierService, logisticsService, userService, systemConfigService, orderService } from '../services/api';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { computeCollectionAmounts, isCreditOrder } from '../utils/payments';
import audioFeedback from '../utils/audioUtils';
import { io } from 'socket.io-client';
import ChangeShippingModal from '../components/ChangeShippingModal';
import UploadEvidenceModal from '../components/UploadEvidenceModal';
import ShippingLabelModal from '../components/ShippingLabelModal';
import { useAuth } from '../context/AuthContext';
import OrderChangesModal from '../components/OrderChangesModal';
import FreePassModal from '../components/FreePassModal';
import CarrierDispatchModal from '../components/CarrierDispatchModal';

function getDeliveryMethodLabel(method) {
  const labels = {
    domicilio_ciudad: 'Domicilio Ciudad',
    domicilio_nacional: 'Domicilio Nacional',
    recogida_tienda: 'Recogida en Tienda',
    envio_nacional: 'Envío Nacional',
    envio_internacional: 'Envío Internacional',
    contraentrega: 'Contraentrega',
    recoge_bodega: 'Recoge en Bodega',
    recoge_bodega_sin_cobro: 'Recoge en Bodega (Sin Cobro)'
  };
  return labels[method] || method || '-';
}

// Helpers mínimos para leer método de pago en crudo desde distintas formas
function __toText(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'object') {
    if (typeof val.label === 'string') return val.label.trim();
    if (typeof val.value === 'string') return val.value.trim();
  }
  return '';
}
function __firstNonEmpty(...vals) {
  for (const v of vals) {
    const s = __toText(v);
    const low = s.toLowerCase();
    if (s && low !== 'undefined' && low !== 'null' && low !== 'n/a' && low !== 'na') {
      return s;
    }
  }
  return '';
}
function __getPath(obj, path) {
  try {
    return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
  } catch {
    return undefined;
  }
}
function __getRawFromPaths(obj, paths) {
  for (const p of paths) {
    const v = __getPath(obj, p);
    const s = __firstNonEmpty(v);
    if (s) return s;
  }
  return '';
}
function getRawPaymentMethod(order) {
  const paths = [
    'payment_method', 'PaymentMethod', 'paymentMethod',
    'payment_method_raw', 'paymentMethodRaw',
    'payment_term', 'paymentTerm',
    'payment_condition', 'paymentCondition',
    'payment_type', 'paymentType',
    'payment', 'metodo_pago', 'metodoPago', 'METODO_PAGO',
    'payment.payment_method', 'payment.method', 'payment.details.method',
    'paymentInfo.method', 'payment_info.method', 'payment_info.payment_method',
    'siigo.payment_method', 'siigo_payment_method',
    'siigoInvoiceData.payment_method', 'invoice_data.payment_method',
    'metadata.payment_method', 'meta.payment_method'
  ];
  return __getRawFromPaths(order, paths) || '-';
}

function getOrderAmount(order) {
  return parseFloat(order.total ?? order.total_amount ?? 0);
}

// Helpers para detectar Camión Externo de forma robusta
function __norm(s) {
  return (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
function isExternalTruckOrder(order, carriersMap) {
  const carrierName =
    (carriersMap?.[order?.carrier_id]?.name) || '';
  const dm = order?.delivery_method || '';
  const ch = order?.channel || order?.shipping_channel || order?.shippingChannel || '';
  const text = [carrierName, dm, ch].map(__norm).join(' ');
  return text.includes('camion') && text.includes('externo');
}

// Determinar si un pedido tiene cambios ESTRUCTURALES (productos/cantidades/notas)
// IGNORA cambios de precio, que son tema de Cartera, no de empaque
const hasProductChanges = (order) => {
  if (!order.packing_snapshot) return false;
  try {
    const snapshot = typeof order.packing_snapshot === 'string'
      ? JSON.parse(order.packing_snapshot)
      : order.packing_snapshot;

    const snapItems = snapshot?.items || [];
    const currentItems = order.items || [];

    // 1. Verificar si cambió la cantidad de ítems (ESTRUCTURAL)
    if (snapItems.length !== currentItems.length) return true;

    // 2. Crear mapas por product_code para comparación robusta
    const snapMap = new Map();
    snapItems.forEach(item => snapMap.set(item.product_code || item.id, item));

    // 3. Verificar si hay productos nuevos o cantidades diferentes (ESTRUCTURAL)
    for (const item of currentItems) {
      const key = item.product_code || item.id;
      const snapItem = snapMap.get(key);

      // Producto nuevo o eliminado
      if (!snapItem) return true;

      // Cantidad cambió (ESTRUCTURAL - requiere re-empaque)
      const qtyChanged = parseInt(snapItem.quantity) !== parseInt(item.quantity);
      if (qtyChanged) return true;

      // NOTA: Ya NO comparamos precios aquí. Los cambios de precio son tema de Cartera.
    }

    // 4. Verificar si hay cambios reales en notas (ignorando tags de sistema)
    const stripSystemTags = (text) => {
      if (!text) return '';
      return text
        .replace(/\[AUTO-RC:[\s\S]*?\](?:\s*\([\s\S]*?\))?/gi, '')
        .replace(/\[WATCHDOG[\s\S]*?\]/gi, '')
        .replace(/Pedido creado desde factura SIIGO:[\s\S]*?\n?/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    /* 
       NOTA: Ya no comparamos notas aquí para bloquear la validación.
       Los cambios de notas deben permitir "Validar Notas" (botón verde), no obligar a "Devolver" (botón rojo).
       La alerta is_modified_after_packing ya nos dice que algo cambió.
    */
    // const currentNotes = stripSystemTags(order.notes || '');
    // const snapNotes = stripSystemTags(snapshot.notes || '');

    // if (currentNotes !== snapNotes) return true;

    return false;
  } catch (e) {
    console.warn('Error validando cambios en productos:', e);
    return true; // Por seguridad, mostrar botón de devolver si falla
  }
};

// Derivar canal de entrega a partir del método y/o carrier
function getChannelLabel(order, carrierName) {
  const dm = (order?.delivery_method || '').toLowerCase();

  // 🆕 Internal orders (INT-): check delivery_method first
  // Only treat as 'Mensajería Local' if NOT a pickup method
  if ((order?.order_number || '').startsWith('INT-')) {
    const isPickup = dm === 'recoge_bodega' || dm === 'recogida_tienda' || dm === 'recoge_posterior' ||
      dm === 'recoge_bodega_sin_cobro' || (dm.includes('recoge') && (dm.includes('bodega') || dm.includes('posterior')));
    if (!isPickup) {
      return 'Mensajería Local';
    }
    // Si es pickup, caer al flujo normal abajo para clasificar como 'Recoge'
  }

  // Entrega en bodega/tienda
  if (
    dm === 'recoge_bodega' ||
    dm === 'recogida_tienda' ||
    dm === 'recoge_bodega_sin_cobro' ||
    (dm.includes('recoge') && (dm.includes('bodega') || dm.includes('posterior')))
  ) {
    return 'Recoge';
  }

  // Mensajería/local (domicilios ciudad/urbana)
  if (dm === 'domicilio' || dm === 'domicilio_local' || dm === 'domicilio_ciudad' || dm === 'mensajeria_urbana') {
    return 'Mensajería Local';
  }

  // Envíos nacionales (transportadora)
  if (dm === 'envio_nacional' || dm === 'nacional' || dm.includes('nacional')) {
    return 'Transportadora';
  }

  // Fallback por nombre del carrier
  const cn = (carrierName || '').toLowerCase();
  if (cn.includes('local')) return 'Mensajería Local';
  if (cn) return 'Transportadora';

  return '-';
}

// Helpers para bloqueo y liberación (Reutilizables en Memos)
// Helpers para bloqueo y liberación (Reutilizables en Memos)
const isCashMethod = (o) => {
  const pm = String(o?.payment_method || '').toLowerCase();
  return ['efectivo', 'contraentrega', 'contado', 'cash'].some(k => pm.includes(k));
};

const isOrderBlocked = (o) => {
  // Si es crédito autorizado, nunca bloquear
  if (isCreditOrder(o)) return false;

  // Usar función centralizada para calcular saldos correctamente
  const { totalDue } = computeCollectionAmounts(o);

  // 🆕 NUEVA LÓGICA: Detectar si es pedido de recogida en bodega
  const deliveryMethod = String(o?.delivery_method || '').toLowerCase();
  const isPickupOrder = deliveryMethod.includes('recoge');
  const isApproved = o.validation_status === 'approved';

  // Para pedidos de recogida en bodega:
  // - Si el saldo está cubierto (<= $2.000), permitir entrega automática
  // - Si hay saldo pendiente, requiere aprobación explícita de Cartera
  // 🆕 FIX: Si hay DEUDA (totalDue > 2000) y NO es crédito, BLOQUEAR incluso si dice 'approved'.
  // Esto corrige el caso donde un abono parcial marca 'approved' pero el pedido no está pagado.
  if (isPickupOrder) {
    if (totalDue <= 2000) return false;
    // Si es crédito, dejar pasar con la lógica estándar (que retorna false al inicio)
    // Si NO es crédito y debe plata, BLOQUEAR.
    return true;
  }

  // Si el saldo es menor o igual a $2.000, no bloquear para otros métodos
  if (totalDue <= 2000) return false;

  // Para domicilios/transportadora con contraentrega/efectivo:
  // - El cobro se realiza AL ENTREGAR
  // - Por tanto, se puede liberar sin validación previa
  if (isCashMethod(o)) {
    return false; // Liberar para cobro al entregar
  }

  // Otros casos con saldo pendiente: bloquear
  return true;
};

const getBlockDetail = (o) => {
  if (!isOrderBlocked(o)) return null;

  // Usar función centralizada para calcular saldos correctamente
  const { totalDue } = computeCollectionAmounts(o);

  // 1. Falta Evidencia (Prioridad alta)
  if (o.is_pending_payment_evidence === 1 || !o.payment_evidence_path) {
    return {
      type: 'missing_evidence',
      label: 'Falta Evidencia',
      subLabel: 'Sin soporte de pago',
      color: 'rose',
      balance: totalDue
    };
  }

  // 2. Pendiente Validación (Tiene soporte pero no está aprobado)
  const vs = String(o.validation_status || '').toLowerCase();
  if (vs !== 'approved' && vs !== 's' && vs !== 'aprobado') {
    return {
      type: 'pending_validation',
      label: 'Por Validar',
      subLabel: 'Pendiente en Cartera',
      color: 'amber',
      balance: totalDue
    };
  }

  // 3. Diferencia de Saldo (Otras causas)
  return {
    type: 'balance_gap',
    label: 'Saldo Incompleto',
    subLabel: 'Mismatch de valores',
    color: 'rose',
    balance: totalDue
  };
};

export default function ReadyToDeliverPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLogistics = user?.role === 'logistica';
  const isAdmin = user?.isSuperAdmin || user?.role === 'admin';

  const handleReturnToPacking = async (orderId) => {
    try {
      await orderService.updateOrder(orderId, { status: 'en_empaque' });
      toast.success('Pedido devuelto a empaque para revisión');
      // Optimista: quitar de la lista
      setOrders(prev => prev.filter(o => o.id !== orderId));
      loadReadyOrders();
    } catch (e) {
      console.error(e);
      toast.error('Error devolviendo a empaque');
    }
  };

  const handleValidateNoteChange = async (orderId) => {
    try {
      await logisticsService.validateNoteChange(orderId);
      toast.success('Nota validada. Pedido listo para entregar');
      // Remover de vista "Devoluciones" automáticamente
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, is_modified_after_packing: 0 } : o
      ));
      loadReadyOrders({ silent: true });
    } catch (e) {
      console.error(e);
      toast.error('Error validando nota');
    }
  };


  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [carriersMap, setCarriersMap] = useState({}); // id -> carrier object
  const [refreshing, setRefreshing] = useState(false);

  // Referencia para controlar actualizaciones sin reiniciar timers
  const modalsOpenRef = React.useRef(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const prevIdsRef = React.useRef(new Set());
  const socketRef = React.useRef(null);

  // Estados de filtros
  const [filters, setFilters] = useState({
    searchText: '',      // Búsqueda por código o cliente
    deliveryDate: '',    // Fecha de entrega específica
    paymentMethod: '',   // Método de pago
    shippingChannel: '', // Canal de envío (Recoge, Mensajería Local, Transportadora)
    status: '',          // Estado de pago/mensajero
    quickFilter: 'all'   // Filtros rápidos (all, liberados, por_liberar)
  });

  // Modales y datos auxiliares
  const [paymentModal, setPaymentModal] = useState({ open: false, order: null, method: 'efectivo', amount: '', notes: '', file: null });
  const [assignModal, setAssignModal] = useState({ open: false, order: null, messengerId: '' });
  const [changeShippingModal, setChangeShippingModal] = useState({ open: false, order: null });
  const [returnModal, setReturnModal] = useState({ open: false, order: null, reason: '' });
  const [freePassModal, setFreePassModal] = useState({ open: false, order: null, action: null });
  const [uploadEvidenceModal, setUploadEvidenceModal] = useState({ open: false, order: null, file: null });
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [selectedOrderForChanges, setSelectedOrderForChanges] = useState(null);
  const [truckGuideModal, setTruckGuideModal] = useState({
    open: false,
    order: null,
    // Conductor
    plate: '',
    driver: '',
    whatsapp: '',
    boxes: '',
    notes: '',
    // Remitente
    sender: {
      name: '',
      nit: '',
      phone: '',
      address: '',
      city: '',
      department: '',
      email: ''
    },
    // Receptor (editable)
    recipient: {
      name: '',
      phone: '',
      address: '',
      city: '',
      department: '',
      nit: '',
      email: ''
    },
    saveSenderDefault: true
  });
  const [messengers, setMessengers] = useState([]);
  const messengersMap = useMemo(() => {
    const map = {};
    (messengers || []).forEach(m => {
      map[m.id] = m;
    });
    return map;
  }, [messengers]);
  const [externalDrivers, setExternalDrivers] = useState([]);
  // UI toggles for compact external truck guide modal
  const [showSenderForm, setShowSenderForm] = useState(false);
  const [showRecipientForm, setShowRecipientForm] = useState(false);

  // Box Count Modal State
  const [boxCountModal, setBoxCountModal] = useState({ open: false, order: null });

  // Carrier Dispatch Acta Modal
  const [dispatchModal, setDispatchModal] = useState({ open: false, orders: [], carrierName: '', carrierId: null });

  // Agrupar pedidos por canal/carrier para mostrar en tarjetas separadas
  const grouped = useMemo(() => {
    // 1. APLICAR FILTROS PRIMERO
    let filteredOrders = orders || [];

    if (filters.searchText.trim()) {
      const search = filters.searchText.toLowerCase().trim();
      filteredOrders = filteredOrders.filter(o => {
        const code = String(o.order_number || o.code || o.id || '').toLowerCase();
        const customer = String(o.customer_name || o.client_name || '').toLowerCase();
        const phone = String(o.customer_phone || o.client_phone || '').toLowerCase();
        const messenger = String(o.messenger_name || messengersMap?.[o.assigned_messenger_id]?.full_name || messengersMap?.[o.assigned_messenger_id]?.name || '').toLowerCase();
        return code.includes(search) || customer.includes(search) || phone.includes(search) || messenger.includes(search);
      });
    }

    if (filters.deliveryDate) {
      filteredOrders = filteredOrders.filter(o => {
        const orderDate = o.delivery_date || o.shipping_date;
        if (!orderDate) return false;
        try {
          return new Date(orderDate).toISOString().split('T')[0] === filters.deliveryDate;
        } catch { return false; }
      });
    }

    if (filters.paymentMethod) {
      filteredOrders = filteredOrders.filter(o => {
        const pm = String(o.payment_method || '').toLowerCase();
        return pm.includes(filters.paymentMethod.toLowerCase());
      });
    }

    if (filters.shippingChannel) {
      filteredOrders = filteredOrders.filter(o => {
        const carrierName = carriersMap?.[o?.carrier_id]?.name || '';
        const channel = getChannelLabel(o, carrierName);
        return channel === filters.shippingChannel;
      });
    }

    // Filtros Rápidos del Dashboard
    if (filters.quickFilter === 'liberados') {
      filteredOrders = filteredOrders.filter(o => !isOrderBlocked(o) && !o.is_modified_after_packing);
    } else if (filters.quickFilter === 'por_liberar') {
      filteredOrders = filteredOrders.filter(o => isOrderBlocked(o) && !o.is_modified_after_packing);
    } else if (filters.quickFilter === 'devolver') {
      filteredOrders = filteredOrders.filter(o => !!o.is_modified_after_packing);
    }

    // 2. LUEGO AGRUPAR (lógica existente)
    const buckets = {
      'Recoge': [],
      'Mensajería Local': [],
      'Otros': []
    };

    filteredOrders.forEach(order => {
      const carrierName = carriersMap?.[order?.carrier_id]?.name || '';
      const channel = getChannelLabel(order, carrierName);

      if (channel === 'Recoge') {
        buckets['Recoge'].push(order);
      } else if (channel === 'Mensajería Local') {
        buckets['Mensajería Local'].push(order);
      } else if (channel === 'Transportadora') {
        const key = carrierName || 'Transportadora (Sin asignar)';
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(order);
      } else {
        buckets['Otros'].push(order);
      }
    });

    const carrierKeys = Object.keys(buckets)
      .filter(k => !['Recoge', 'Mensajería Local', 'Otros'].includes(k))
      .sort((a, b) => a.localeCompare(b, 'es'));

    const finalizeGroup = (key, title, items) => {
      const helperGroupDates = (list) => {
        const dateBuckets = {};
        list.forEach(item => {
          let dateVal = item.delivery_date || item.shipping_date;
          let dateKey = 'No asignada';
          if (dateVal) {
            try {
              dateKey = new Date(dateVal).toISOString().split('T')[0];
            } catch (e) {
              dateKey = 'Error en fecha';
            }
          }
          if (!dateBuckets[dateKey]) dateBuckets[dateKey] = [];
          dateBuckets[dateKey].push(item);
        });

        return Object.keys(dateBuckets)
          .sort((a, b) => {
            if (a === 'No asignada') return 1;
            if (b === 'No asignada') return -1;
            return a.localeCompare(b);
          })
          .map(dk => {
            let dateLabel = dk;
            if (dk !== 'No asignada' && dk !== 'Error en fecha') {
              try {
                dateLabel = new Date(dk + 'T12:00:00Z').toLocaleDateString('es-CO', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
              } catch (e) {
                dateLabel = dk;
              }
            }
            return {
              dateKey: dk,
              dateLabel,
              items: dateBuckets[dk]
            };
          });
      };

      if (key === 'Mensajería Local') {
        const messengerBuckets = {}; // { messengerName: items[] }
        items.forEach(item => {
          const mNameRaw = item.messenger_name || item.assigned_messenger_name;
          const mName = mNameRaw || 'Sin Asignar';

          if (!messengerBuckets[mName]) messengerBuckets[mName] = [];
          messengerBuckets[mName].push(item);
        });

        const messengerGroups = Object.keys(messengerBuckets)
          .sort((a, b) => {
            if (a === 'Sin Asignar') return 1;
            if (b === 'Sin Asignar') return -1;
            return a.localeCompare(b);
          })
          .map(mName => {
            const grpItems = messengerBuckets[mName];
            const totalCount = grpItems.length;
            const acceptedCount = grpItems.filter(i => {
              const s = String(i?.messenger_status || i?.status || '').toLowerCase();
              return ['accepted', 'in_delivery', 'en_reparto'].includes(s) || i.messenger_accepted === true;
            }).length;
            const pendingCount = totalCount - acceptedCount;

            return {
              messengerName: mName,
              isAccepted: acceptedCount > 0 && mName !== 'Sin Asignar',
              totalCount,
              acceptedCount,
              pendingCount,
              subGroups: helperGroupDates(grpItems)
            };
          });

        return { key, title, items, messengerGroups, isLocal: true };
      }

      return { key, title, items, subGroups: helperGroupDates(items) };
    };

    return [
      finalizeGroup('Recoge', `Recoge (${buckets['Recoge'].length})`, buckets['Recoge']),
      finalizeGroup('Mensajería Local', `Mensajería Local (${buckets['Mensajería Local'].length})`, buckets['Mensajería Local']),
      ...carrierKeys.map(k => finalizeGroup(k, `${k} (${buckets[k].length})`, buckets[k])),
      finalizeGroup('Otros', `Otros (${buckets['Otros'].length})`, buckets['Otros'])
    ];
  }, [orders, carriersMap, filters]);

  // KPI Metrics - SIEMPRE muestra totales SIN filtros
  const kpiMetrics = useMemo(() => {
    // NO aplicar ningún filtro - siempre mostrar el total de todos los pedidos
    const baseOrders = orders || [];

    const stats = {
      total: baseOrders.length,
      liberados: baseOrders.filter(o => !isOrderBlocked(o) && !o.is_modified_after_packing).length,
      porLiberar: baseOrders.filter(o => isOrderBlocked(o) && !o.is_modified_after_packing).length,
      devoluciones: baseOrders.filter(o => !!o.is_modified_after_packing).length,
      recoge: { total: 0, liberados: 0, bloqueados: 0 },
      mensajeria: { total: 0, aceptados: 0, sinAsignar: 0, asignados: 0, breakdown: {} },
      posterior: { total: 0, liberados: 0 },
      transportadoras: { total: 0, asignadas: 0, sinAsignar: 0, breakdown: {} },
      externa: { total: 0, asignados: 0 }
    };

    baseOrders.forEach(o => {
      const carrierName = carriersMap?.[o?.carrier_id]?.name || '';
      const channelLabel = getChannelLabel(o, carrierName);
      const isBlocked = isOrderBlocked(o);

      if (channelLabel === 'Recoge') {
        stats.recoge.total++;
        if (isBlocked) stats.recoge.bloqueados++;
        else stats.recoge.liberados++;
      } else if (channelLabel === 'Mensajería Local') {
        stats.mensajeria.total++;
        if (!o.assigned_messenger_id) {
          stats.mensajeria.sinAsignar++;
        } else {
          stats.mensajeria.asignados++;
          const mName = messengersMap?.[o.assigned_messenger_id]?.name || messengersMap?.[o.assigned_messenger_id]?.full_name || `Mensajero #${o.assigned_messenger_id}`;
          if (!stats.mensajeria.breakdown[mName]) stats.mensajeria.breakdown[mName] = { asignados: 0, aceptados: 0, pendientes: 0 };
          stats.mensajeria.breakdown[mName].asignados++;

          const s = String(o?.messenger_status || o?.status || '').toLowerCase();
          const isAcc = ['accepted', 'in_delivery', 'en_reparto'].includes(s) || o.messenger_accepted === true;
          if (isAcc) {
            stats.mensajeria.breakdown[mName].aceptados++;
            stats.mensajeria.aceptados++;
          } else {
            stats.mensajeria.breakdown[mName].pendientes++;
          }
        }
      } else if (channelLabel === 'Recoge Posterior' || (o?.delivery_method || '').toLowerCase().includes('posterior')) {
        stats.posterior.total++;
        if (!isBlocked) stats.posterior.liberados++;
      } else if (channelLabel === 'Transportadora') {
        stats.transportadoras.total++;
        if (o.carrier_id) {
          stats.transportadoras.asignadas++;
          const cName = carriersMap?.[o.carrier_id]?.name || `Carrier #${o.carrier_id}`;
          if (!stats.transportadoras.breakdown[cName]) stats.transportadoras.breakdown[cName] = { total: 0, liberados: 0 };
          stats.transportadoras.breakdown[cName].total++;
          if (!isBlocked) stats.transportadoras.breakdown[cName].liberados++;
        } else {
          stats.transportadoras.sinAsignar++;
        }
      } else if (isExternalTruckOrder(o, carriersMap) || (o?.delivery_method || '').toLowerCase().includes('externa')) {
        stats.externa.total++;
        if (o.carrier_id) stats.externa.asignados++;
      }
    });

    return stats;
  }, [orders, messengersMap, carriersMap]);


  const loadReadyOrders = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      setError('');

      // Usar endpoint consolidado de Logística que expone flags de caja/cartera
      const resp = await logisticsService.getReadyForDelivery({ _ts: Date.now() });
      const grouped = resp?.data?.groupedOrders || resp?.groupedOrders || {};
      // Aplanar grupos en una sola lista
      const merged = Object.values(grouped).reduce((acc, list) => acc.concat(list || []), []);

      setOrders(merged);
      try {
        prevIdsRef.current = new Set((merged || []).map(o => Number(o.id)));
        setLastUpdated(new Date().toISOString());
      } catch (_) { }
    } catch (e) {
      console.error('Error cargando pedidos listos para entregar:', e);
      setError('Error cargando pedidos listos para entregar');
      toast.error('Error cargando pedidos listos para entregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReadyOrders();
  }, [loadReadyOrders]);

  // Sincronizar ref de modales
  useEffect(() => {
    modalsOpenRef.current =
      paymentModal.open || assignModal.open || truckGuideModal.open || changeShippingModal.open || returnModal.open || uploadEvidenceModal.open || freePassModal.open;
  }, [paymentModal.open, assignModal.open, truckGuideModal.open, changeShippingModal.open, returnModal.open, uploadEvidenceModal.open, freePassModal.open]);

  // Carga silenciosa para polling
  const silentReload = useCallback(async () => {
    if (modalsOpenRef.current) return; // No actualizar si hay modales abiertos


    try {
      const resp = await logisticsService.getReadyForDelivery({ _ts: Date.now() });
      const groupedData = resp?.data?.groupedOrders || resp?.groupedOrders || {};
      const merged = Object.values(groupedData).reduce((acc, list) => acc.concat(list || []), []);

      // Actualizar estado sin activar loading spinner y detectar nuevos pedidos
      try {
        const prev = prevIdsRef.current || new Set();
        const current = new Set((merged || []).map(o => Number(o.id)));
        let newCount = 0;
        for (const id of current) {
          if (!prev.has(id)) newCount++;
        }
        setOrders(merged);
        prevIdsRef.current = current;
        setLastUpdated(new Date().toISOString());
        if (newCount > 0) {
          toast.success(`${newCount} pedido(s) nuevo(s) listos para entregar`);
          try { audioFeedback.playStatusAlert(); } catch { }
        }
      } catch {
        setOrders(merged);
      }
    } catch (e) {
      console.error('Error en auto-actualización:', e);
    }
  }, []);

  // Deshabilitado: evitamos auto-refresh periódico para Logística.
  // La página se actualiza por:
  // - WebSocket 'order-status-changed' (tiempo real)
  // - Evento de foco/visibilidad (al volver a la pestaña)
  // - Botón "Actualizar"
  // Si se necesitara un fallback, considerar un polling con backoff y solo si el socket no está disponible.

  // Refrescar al volver el foco o pestaña visible
  useEffect(() => {
    const onFocus = () => silentReload();
    const onVisibility = () => {
      if (!document.hidden) silentReload();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [silentReload]);

  // Socket: refresco instantáneo como en Empaque (sin F5)
  useEffect(() => {
    try {
      const socket = io(window.location.origin, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        withCredentials: true
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        try { socket.emit('join-orders-updates'); } catch { }
      });

      const handleStatusChanged = (payload) => {
        try {
          const to = String(payload?.to_status || '').toLowerCase();
          const from = String(payload?.from_status || '').toLowerCase();

          // Nuevo pedido listo para entregar -> alertar y recargar
          if (to === 'listo_para_entrega') {
            toast.success('🆕 Pedido listo para entregar');
            silentReload();
            return;
          }

          // Cambios que sacan o mueven pedidos de esta vista -> recargar silencioso
          const affectsList = (
            from === 'listo_para_entrega' ||
            ['en_reparto', 'entregado_transportadora', 'entregado_cliente', 'cancelado', 'enviado'].includes(to)
          );
          if (affectsList) {
            silentReload();
          }
        } catch { }
      };

      socket.on('order-status-changed', handleStatusChanged);

      // 🆕 Listener específico para validación de pagos (sin cambio de estado)
      socket.on('order-payment-validated', (payload) => {
        console.log('💰 Pago validado recibido por socket:', payload);
        silentReload(); // Recargar silenciosamente para actualizar balance/bloqueo
      });

      // 🆕 Listeners adicionales para pagos en efectivo y actualizaciones generales
      socket.on('cash_box_update', (payload) => {
        console.log('💵 Movimiento de caja recibido:', payload);
        // Si es un ingreso (pago), actualizar vista
        if (payload?.type === 'income' || payload?.type === 'income_synced' || payload?.type === 'expense') {
          silentReload();
        }
      });

      socket.on('order-updated', (payload) => {
        console.log('🔄 Actualización de pedido recibida:', payload);
        silentReload();
      });

      return () => {
        try {
          socket.off('order-status-changed', handleStatusChanged);
          socket.disconnect();
        } catch { }
        socketRef.current = null;
      };
    } catch {
      // No bloquear si socket falla
    }
  }, [silentReload]);

  // 🆕 Auto-refresh cada 30 segundos para detectar cambios en validation_status
  // (validaciones de Cartera que no emiten eventos WebSocket)
  useEffect(() => {
    const intervalId = setInterval(() => {
      silentReload();
    }, 30000); // 30 segundos

    return () => clearInterval(intervalId);
  }, [silentReload]);

  // Cargar transportadoras activas para mapear carrier_id -> nombre
  useEffect(() => {
    (async () => {
      try {
        const resp = await carrierService.getActive();
        const list = resp?.data || [];
        const map = {};
        list.forEach(c => { if (c?.id != null) map[c.id] = c; });
        setCarriersMap(map);
      } catch (e) {
        // No bloquear la vista si falla
        console.error('Error cargando carriers:', e?.message || e);
      }
    })();
  }, []);

  // Cargar mensajeros activos para asignación (solo una vez)
  useEffect(() => {
    (async () => {
      try {
        const resp = await userService.getUsers({ role: 'mensajero', active: true });
        setMessengers(resp?.data?.users || resp?.data || resp?.users || []);
      } catch (e) {
        console.error('Error cargando mensajeros:', e?.message || e);
      }
    })();
  }, []);

  // Cargar conductores externos
  const loadExternalDrivers = useCallback(async () => {
    try {
      const resp = await logisticsService.getExternalDrivers();
      setExternalDrivers(resp?.data || []);
    } catch (e) {
      console.error('Error cargando conductores externos:', e);
    }
  }, []);

  useEffect(() => {
    loadExternalDrivers();
  }, [loadExternalDrivers]);

  // Descargar planilla PDF por transportadora para un grupo
  const downloadCarrierManifest = async (group) => {
    try {
      const first = (group.items || [])[0];
      const carrierId = first?.carrier_id;
      if (!carrierId) {
        toast.error('No hay transportadora asignada para este grupo');
        return;
      }
      // Permitir descarga aunque la transportadora esté inactiva (backend soporta inactivas)
      const carrier = carriersMap?.[carrierId];
      if (!carrier) {
        console.warn('Transportadora inactiva o no listada; continuando con descarga de planilla');
      }

      const resp = await logisticsService.downloadCarrierManifest({ carrierId });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = (() => {
        const cd = resp.headers?.['content-disposition'] || '';
        const m = cd.match(/filename="?([^"]+)"?/i);
        return m ? m[1] : `planilla-${group.key}-${new Date().toISOString().slice(0, 10)}.pdf`;
      })();
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Planilla descargada');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo descargar la planilla');
    }
  };

  // Descargar planilla PDF para Mensajería Local
  // - Si se pasa messengerId, filtra por ese mensajero
  // - Si no, descarga la planilla agregada
  const downloadLocalManifest = async (messengerId) => {
    try {
      const params = messengerId ? { messengerId } : {};
      const resp = await logisticsService.downloadLocalManifest(params);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = (() => {
        const cd = resp.headers?.['content-disposition'] || '';
        const m = cd.match(/filename="?([^"]+)"?/i);
        if (m) return m[1];
        const date = new Date().toISOString().slice(0, 10);
        const suffix = messengerId ? `mensajeria_local_${String(messengerId)}` : 'mensajeria_local';
        return `planilla-${suffix}-${date}.pdf`;
      })();
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Planilla de Mensajería Local descargada');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo descargar la planilla de Mensajería Local');
    }

  };


  const handleUploadEvidence = async () => {
    try {
      if (!uploadEvidenceModal.file) {
        toast.error('Seleccione un archivo');
        return;
      }
      const formData = new FormData();
      formData.append('photo', uploadEvidenceModal.file);

      await logisticsService.uploadPaymentEvidence(uploadEvidenceModal.order.id, formData);

      toast.success('Comprobante subido exitosamente');
      setUploadEvidenceModal({ open: false, order: null, file: null });
      loadReadyOrders();
    } catch (error) {
      console.error('Error subiendo comprobante:', error);
      toast.error('Error al subir el comprobante');
    }
  };

  const isCarrierGroup = (group) =>
    !['Recoge', 'Mensajería Local', 'Otros'].includes(group?.key) &&
    (group?.items || []).some(o => o?.carrier_id);

  // Detectar grupo de Mensajería Local de forma tolerante (con/sin tildes, variantes)
  const isLocalGroupKey = (key) => {
    const k = __norm(String(key || ''));
    return k.includes('mensajeria local') || k === 'mensajeria_local';
  };
  // En algunos escenarios el key puede variar; usar también el title como respaldo
  const isLocalGroup = (g) => {
    const byKey = isLocalGroupKey(g?.key);
    const titleNorm = __norm(String(g?.title || ''));
    const byTitle = titleNorm.includes('mensajeria local');
    return byKey || byTitle;
  };
  // Fallback definitivo: detectar por los ítems del grupo (derivando canal por pedido)
  const isLocalGroupByItems = (g) => {
    try {
      return (g?.items || []).some(o => {
        const carrierName = carriersMap?.[o?.carrier_id]?.name || '';
        return getChannelLabel(o, carrierName) === 'Mensajería Local';
      });
    } catch {
      return false;
    }
  };
  // Detección adicional: por método de entrega de los ítems (domicilios / mensajería urbana)
  const isLocalByMethod = (g) => {
    try {
      return (g?.items || []).some(o => {
        const dm = String(o?.delivery_method || '').toLowerCase();
        return (
          dm === 'domicilio' ||
          dm === 'domicilio_local' ||
          dm === 'domicilio_ciudad' ||
          dm === 'mensajeria_urbana' ||
          (dm.includes('domicilio') && !dm.includes('nacional'))
        );
      });
    } catch {
      return false;
    }
  };

  // Variantes de color (estáticas) para mejorar visibilidad de los botones por mensajero.
  // Usamos una lista de clases Tailwind explícitas para evitar problemas con el purge/JIT.
  const messengerColorClass = (idx) => {
    const variants = [
      'bg-indigo-600 hover:bg-indigo-700',
      'bg-emerald-600 hover:bg-emerald-700',
      'bg-amber-600 hover:bg-amber-700',
      'bg-rose-600 hover:bg-rose-700',
      'bg-sky-600 hover:bg-sky-700',
      'bg-violet-600 hover:bg-violet-700',
      'bg-teal-600 hover:bg-teal-700',
    ];
    return variants[idx % variants.length];
  };

  // Abrir/Imprimir guía en nueva pestaña para Transportadora (reimpresión siempre disponible)
  const openGuideInNewTab = (ord, boxCount = 1) => {
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/logistics/generate-guide-html';
      form.target = '_blank';

      const formDataGuide = {
        orderId: ord?.id,
        boxCount: boxCount,
        // Notas opcionales
        notes: [ord?.siigo_observations, ord?.observations, ord?.notes].filter(Boolean).join('\n')
      };

      Object.keys(formDataGuide).forEach((key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = formDataGuide[key] || '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      form.remove();
      toast.success('Guía abierta en nueva ventana. Use Ctrl+P para imprimir o guardar como PDF');
    } catch (error) {
      console.error('Error generando guía:', error);
      toast.error('Error generando guía de envío');
    }
  };

  return (
    <div className="p-6" >
      <ShippingLabelModal
        isOpen={boxCountModal.open}
        onClose={() => setBoxCountModal({ open: false, order: null })}
        order={boxCountModal.order}
        onConfirm={(count) => {
          openGuideInNewTab(boxCountModal.order, count);
        }}
      />
      {/* Sticky Header Compacto (Glassmorphism) */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm overflow-hidden">
        {/* Fila 1: Título y Acciones Inline (Súper Compacto) */}
        <div className="px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-black text-gray-900 uppercase tracking-tight flex-shrink-0">Pedidos por Entregar</h1>
            <div className="hidden md:flex items-center gap-2 text-[10px] text-gray-400 font-medium whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Actualizado: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString('es-CO') : '-'}
            </div>
          </div>

          <div className="flex items-center gap-1.5">

            <button
              onClick={async () => { try { setRefreshing(true); await loadReadyOrders(); } finally { setRefreshing(false); } }}
              className="px-3 py-1.5 text-[11px] font-bold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center shadow-sm"
            >
              <Icons.RefreshCw className={`w-3.5 h-3.5 mr-1.5 text-emerald-600 ${refreshing ? 'animate-spin' : ''}`} />
              REFRESCAR
            </button>
            <button
              onClick={() => navigate('/logistics/handoff')}
              className="flex items-center px-4 py-2 bg-slate-900 text-white text-[11px] font-black rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all uppercase tracking-widest mr-2"
            >
              <Icons.Truck className="w-3.5 h-3.5 mr-1.5" />
              HANDOFF
            </button>
            <button
              onClick={() => {
                downloadLocalManifest();
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white text-[11px] font-black rounded-xl shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all uppercase tracking-widest"
            >
              <Icons.Download className="w-3.5 h-3.5 mr-1.5" />
              PLANILLA
            </button>
          </div>
        </div>

        {/* Dashboard Horizontal de 5 Canales (Diseño de Tablas) */}
        {!loading && orders.length > 0 && (
          <div className="px-4 pb-4 flex items-stretch gap-4 overflow-x-auto no-scrollbar bg-white/50">
            {/* Componente Genérico de Tarjeta KPI con Tabla */}
            {(() => {
              const KPICard = ({ title, icon: Icon, total, tableData, channelValue, colorClass }) => (
                <div
                  onClick={() => setFilters(f => ({ ...f, shippingChannel: channelValue }))}
                  className={`flex-1 min-w-[280px] bg-white border rounded-xl shadow-sm overflow-hidden flex items-stretch transition-all cursor-pointer hover:shadow-md active:scale-[0.99] ${filters.shippingChannel === channelValue ? `ring-2 ring-offset-1 ${colorClass.ring} border-transparent` : 'border-gray-100'}`}
                >
                  {/* Lado Izquierdo: Número Gigante */}
                  <div className={`w-24 flex flex-col items-center justify-center border-r ${filters.shippingChannel === channelValue ? colorClass.bgActive : 'bg-gray-50'}`}>
                    <span className={`text-3xl font-black ${filters.shippingChannel === channelValue ? colorClass.textActive : 'text-gray-900'}`}>{total}</span>
                    <div className="flex items-center gap-1 mt-1">
                      <Icon className={`w-3 h-3 ${filters.shippingChannel === channelValue ? colorClass.textActive : 'text-gray-400'}`} />
                      <span className={`text-[9px] uppercase font-black tracking-tighter ${filters.shippingChannel === channelValue ? colorClass.textActive : 'text-gray-400'}`}>{title}</span>
                    </div>
                  </div>
                  {/* Lado Derecho: Mini Tabla */}
                  <div className="flex-1 p-2.5 flex flex-col justify-center">
                    <table className="w-full text-[10px] leading-tight">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-1 font-black text-gray-400 uppercase tracking-tighter">{tableData.headerLeft || 'Concepto'}</th>
                          <th className="text-right py-1 font-black text-gray-400 uppercase tracking-tighter">#</th>
                          {tableData.headerRight && <th className="text-right py-1 font-black text-gray-400 uppercase tracking-tighter">!</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {tableData.rows.map((row, idx) => (
                          <tr
                            key={idx}
                            onClick={row.onClick}
                            className={`hover:bg-gray-50/50 ${row.onClick ? 'cursor-pointer' : ''}`}
                          >
                            <td className="py-1 font-bold text-gray-700 truncate max-w-[100px]">{row.label}</td>
                            <td className="py-1 text-right font-black text-gray-900">{row.value}</td>
                            {tableData.headerRight && (
                              <td className="py-1 text-right font-black text-rose-600">{row.alert || '-'}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );

              return (
                <>
                  {/* 1. RECOGE */}
                  <KPICard
                    title="Recoge"
                    icon={Icons.Store}
                    total={kpiMetrics.recoge.total}
                    channelValue="Recoge"
                    colorClass={{ ring: 'ring-blue-500', bgActive: 'bg-blue-600', textActive: 'text-white' }}
                    tableData={{
                      headerLeft: 'Estado',
                      rows: [
                        { label: 'Liberados', value: kpiMetrics.recoge.liberados, onClick: (e) => { e.stopPropagation(); setFilters(f => ({ ...f, shippingChannel: 'Recoge', quickFilter: 'liberados' })); } },
                        { label: 'Bloqueados', value: kpiMetrics.recoge.bloqueados, onClick: (e) => { e.stopPropagation(); setFilters(f => ({ ...f, shippingChannel: 'Recoge', quickFilter: 'por_liberar' })); } }
                      ]
                    }}
                  />

                  {/* 2. LOCAL */}
                  <KPICard
                    title="Local"
                    icon={Icons.Bike}
                    total={kpiMetrics.mensajeria.total}
                    channelValue="Mensajería Local"
                    colorClass={{ ring: 'ring-emerald-500', bgActive: 'bg-emerald-600', textActive: 'text-white' }}
                    tableData={{
                      headerLeft: 'Mensajero',
                      headerRight: 'Pend',
                      rows: [
                        ...Object.entries(kpiMetrics.mensajeria.breakdown).slice(0, 3).map(([name, s]) => ({
                          label: name, value: s.asignados, alert: s.pendientes > 0 ? s.pendientes : null,
                          onClick: (e) => { e.stopPropagation(); setFilters(f => ({ ...f, shippingChannel: 'Mensajería Local', searchText: name })); }
                        })),
                        { label: 'Sin Asignar', value: kpiMetrics.mensajeria.sinAsignar, onClick: (e) => { e.stopPropagation(); setFilters(f => ({ ...f, shippingChannel: 'Mensajería Local', searchText: 'Sin Asignar' })); } }
                      ]
                    }}
                  />

                  {/* 3. POSTERIOR */}
                  <KPICard
                    title="Posterior"
                    icon={Icons.Clock}
                    total={kpiMetrics.posterior.total}
                    channelValue="Recoge Posterior"
                    colorClass={{ ring: 'ring-amber-500', bgActive: 'bg-amber-600', textActive: 'text-white' }}
                    tableData={{
                      headerLeft: 'Estado',
                      rows: [
                        { label: 'Liberados', value: kpiMetrics.posterior.liberados },
                        { label: 'Pendientes', value: kpiMetrics.posterior.total - kpiMetrics.posterior.liberados }
                      ]
                    }}
                  />

                  {/* 4. NACIONAL */}
                  <KPICard
                    title="Nacional"
                    icon={Icons.Truck}
                    total={kpiMetrics.transportadoras.total}
                    channelValue="Transportadora"
                    colorClass={{ ring: 'ring-purple-500', bgActive: 'bg-purple-600', textActive: 'text-white' }}
                    tableData={{
                      headerLeft: 'Transportadora',
                      rows: [
                        ...Object.entries(kpiMetrics.transportadoras.breakdown).slice(0, 3).map(([name, s]) => ({
                          label: name, value: s.total
                        })),
                        { label: 'Sin Guía', value: kpiMetrics.transportadoras.sinAsignar }
                      ]
                    }}
                  />

                  {/* 5. EXTERNA */}
                  <KPICard
                    title="Externa"
                    icon={Icons.Package}
                    total={kpiMetrics.externa.total}
                    channelValue="Mensajería Externa"
                    colorClass={{ ring: 'ring-rose-500', bgActive: 'bg-rose-600', textActive: 'text-white' }}
                    tableData={{
                      headerLeft: 'Estado',
                      rows: [
                        { label: 'Asignados', value: kpiMetrics.externa.asignados },
                        { label: 'Pendientes', value: kpiMetrics.externa.total - kpiMetrics.externa.asignados }
                      ]
                    }}
                  />
                </>
              );
            })()}
          </div>
        )}

        {/* Detalle Desagregado por Mensajero (Solo cuando Local está activo) */}
        {!loading && filters.shippingChannel === 'Mensajería Local' && kpiMetrics.mensajeria.asignados > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-2 bg-white/30">
            {Object.entries(kpiMetrics.mensajeria.breakdown).map(([name, stats]) => (
              <div key={name} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-100 shadow-sm">
                <div className="p-1 bg-emerald-100 rounded-md">
                  <Icons.User className="w-3 h-3 text-emerald-600" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-gray-800 uppercase tracking-tight leading-none">{name}</span>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[9px] font-bold text-gray-500">Asign: <strong>{stats.asignados}</strong></span>
                    <span className={`text-[9px] font-bold ${stats.pendientes > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {stats.pendientes > 0 ? `Faltan: ${stats.pendientes}⚠` : 'OK ✓'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Panel de Filtros (Optimizado al máximo) */}
        <div className="px-4 py-2 bg-gray-50/50 border-t border-gray-100 flex items-center gap-3">
          {/* Búsqueda por texto */}
          <div className="flex-1 max-w-sm relative">
            <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar pedido, cliente..."
              value={filters.searchText}
              onChange={(e) => setFilters(f => ({ ...f, searchText: e.target.value }))}
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>

          {/* Canal de Envío */}
          <div className="w-40">
            <select
              value={filters.shippingChannel}
              onChange={(e) => setFilters(f => ({ ...f, shippingChannel: e.target.value }))}
              className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
            >
              <option value="">TODOS LOS CANALES</option>
              <option value="Recoge">RECOGE</option>
              <option value="Mensajería Local">LOCAL</option>
              <option value="Recoge Posterior">POSTERIOR</option>
              <option value="Transportadora">NACIONAL</option>
              <option value="Mensajería Externa">EXTERNA</option>
            </select>
          </div>

          {/* Fecha de Entrega */}
          <div className="w-32">
            <input
              type="date"
              value={filters.deliveryDate}
              onChange={(e) => setFilters(f => ({ ...f, deliveryDate: e.target.value }))}
              className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
            />
          </div>

          {/* Botón Reset */}
          <button
            onClick={() => setFilters({ searchText: '', deliveryDate: '', paymentMethod: '', shippingChannel: '', status: '', quickFilter: 'all' })}
            className="p-1.5 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
            title="Limpiar filtros"
          >
            <Icons.X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-content p-0">
          {loading ? (
            <div className="p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-48 mb-3"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-red-600">{error}</div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Icons.Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              No hay pedidos listos para entregar.
            </div>
          ) : (
            <>
              {grouped.filter(g => (g.items || []).length > 0).map(g => (
                <div key={g.key} className="mb-8">
                  {/* Debug: registro de detección para cada grupo */}
                  {(() => {
                    try {
                      const byKey = isLocalGroup(g);
                      const byItems = isLocalGroupByItems(g);
                      const byMethod = isLocalByMethod(g);
                      // Log visible en consola del navegador
                      // eslint-disable-next-line no-console
                      console.log('[ReadyToDeliver] Grupo:', { key: g?.key, title: g?.title, byKey, byItems, byMethod });
                    } catch (e) {
                      // noop
                    }
                    return null;
                  })()}
                  <div className="px-6 pt-4 pb-2 flex items-center justify-between flex-nowrap bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-200">
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Icono según tipo de grupo */}
                      {g.key === 'Recoge' && <Icons.Store className="w-6 h-6 text-blue-600 flex-shrink-0" />}
                      {g.key === 'Mensajería Local' && <Icons.Bike className="w-6 h-6 text-emerald-600 flex-shrink-0" />}
                      {isCarrierGroup(g) && <Icons.Truck className="w-6 h-6 text-purple-600 flex-shrink-0" />}
                      {g.key === 'Otros' && <Icons.Package className="w-6 h-6 text-gray-600 flex-shrink-0" />}

                      <h2 className="text-xl font-black text-gray-900 flex-shrink-0 mr-3 tracking-tight">{g.title}</h2>

                      {/* Badge de conteo total más prominente */}
                      <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-black bg-white text-blue-700 shadow-sm border border-blue-100 uppercase tracking-widest">
                        {g.items.length} pedidos
                      </span>
                      {(() => {
                        try {
                          const byKey = isLocalGroup(g);
                          const byItems = isLocalGroupByItems(g);
                          const byMethod = isLocalByMethod(g);
                          const byTitle = __norm(String(g?.title || '')).includes('mensajeria local');
                          const byKeyText = __norm(String(g?.key || '')).includes('mensajeria local');
                          const isLocal = byKey || byItems || byMethod || byTitle || byKeyText;
                          if (!isLocal) return null;

                          // Construir lista de mensajeros con conteos
                          const map = {};
                          const tryParseId = (v) => {
                            const n = Number(v);
                            return Number.isFinite(n) && n > 0 ? n : null;
                          };
                          const findIdByName = (label) => {
                            const t = __norm(String(label || ''));
                            if (!t) return null;
                            const m = (messengers || []).find(mm => {
                              const pool = [mm?.full_name, mm?.username, mm?.name].map(x => __norm(String(x || '')));
                              return pool.includes(t);
                            });
                            return m ? Number(m.id) : null;
                          };
                          (g.items || []).forEach(o => {
                            let id =
                              tryParseId(o?.assigned_messenger_id) ??
                              tryParseId(o?.assigned_messenger);
                            if (!id) {
                              id = findIdByName(o?.messenger_name) || findIdByName(o?.assigned_messenger_name);
                            }
                            const fallbackName =
                              (messengers || []).find(m => Number(m.id) === id)?.full_name ||
                              (id ? `ID ${id}` : null);
                            const name = o?.messenger_name || o?.assigned_messenger_name || fallbackName;
                            if (!id || !name) return;
                            if (!map[id]) map[id] = { id, name, count: 0 };
                            map[id].count += 1;
                          });
                          const list = Object.values(map).sort((a, b) => a.name.localeCompare(b.name, 'es'));
                          if (list.length === 0) return null;

                          // Chips a la derecha del título (con scroll horizontal)
                          return (
                            <div className="ml-3 flex-1 min-w-0 flex items-center gap-2 overflow-x-auto whitespace-nowrap pr-2">
                              {list.map((m, i) => (
                                <button
                                  key={m.id}
                                  onClick={() => downloadLocalManifest(m.id)}
                                  className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full text-white shadow-sm ring-1 ring-black/5 ${messengerColorClass(i)}`}
                                  title={`Descargar planilla de ${m.name}`}
                                >
                                  <Icons.Download className="w-3 h-3 mr-1 opacity-90" />
                                  {m.name} ({m.count})
                                </button>
                              ))}
                            </div>
                          );
                        } catch {
                          return null;
                        }
                      })()}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">

                      {isCarrierGroup(g) && (
                        <>
                          <button
                            onClick={() => setDispatchModal({ open: true, orders: g.items || [], carrierName: g.key, carrierId: (g.items || [])[0]?.carrier_id || null })}
                            className="btn bg-emerald-600 hover:bg-emerald-700 text-white"
                            title="Cerrar Despacho - Generar Acta de entrega a transportadora"
                          >
                            <Icons.FileCheck className="w-4 h-4 mr-2" />
                            Cerrar Despacho
                          </button>
                          <button
                            onClick={() => downloadCarrierManifest(g)}
                            className="btn btn-primary"
                            title="Descargar planilla para esta transportadora"
                          >
                            <Icons.Download className="w-4 h-4 mr-2" />
                            Descargar planilla
                          </button>
                        </>
                      )}

                      {(() => {
                        try {
                          const byKey = isLocalGroup(g);
                          const byItems = isLocalGroupByItems(g);
                          const byMethod = isLocalByMethod(g);
                          const byTitle = __norm(String(g?.title || '')).includes('mensajeria local');
                          const byKeyText = __norm(String(g?.key || '')).includes('mensajeria local');
                          const directKey = String(g?.key || '') === 'Mensajería Local';
                          const anyLocal = directKey || byKey || byItems || byMethod || byTitle || byKeyText;
                          return anyLocal ? (
                            <button
                              onClick={downloadLocalManifest}
                              className="btn btn-primary"
                              title="Descargar planilla agregada de Mensajería Local"
                            >
                              <Icons.Download className="w-4 h-4 mr-2" />
                              Descargar planilla
                            </button>
                          ) : null;
                        } catch {
                          return null;
                        }
                      })()}
                    </div>
                  </div>
                  {/* Mensajería Local: chips renderizados en la barra de acciones (derecha). Bloque viejo eliminado. */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-700">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-gray-600">
                            Pedido
                          </th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-gray-600">
                            Cliente
                          </th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-gray-600">
                            Entrega
                          </th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-white uppercase tracking-wider border-r border-gray-600">
                            Canal
                          </th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            Monto
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          const renderRows = (subGroups, messengerContext = null) => (
                            subGroups.map((sub) => (
                              <React.Fragment key={messengerContext ? `${messengerContext.name}-${sub.dateKey}` : sub.dateKey}>
                                {/* Fila de Sub-agrupación por Fecha */}
                                <tr className="bg-gray-50/80">
                                  <td colSpan="5" className="px-6 py-2">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2 text-[10px] font-black text-blue-900 uppercase tracking-widest pl-2">
                                        <Icons.Calendar className="w-3.5 h-3.5 text-blue-600" />
                                        Fecha de Entrega: <span className="text-blue-700 underline underline-offset-4 decoration-2 decoration-blue-200">{sub.dateLabel}</span>
                                      </div>
                                      {/* Contador más visible */}
                                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-black border border-blue-200 uppercase tracking-tighter">
                                        {sub.items.length} {sub.items.length === 1 ? 'pedido' : 'pedidos'}
                                      </span>
                                    </div>
                                  </td>
                                </tr>

                                {sub.items.map((order) => {
                                  const pm = String(order.payment_method || '').toLowerCase();
                                  const isCredit = isCreditOrder(order);
                                  const isCashMethod = ['efectivo', 'contraentrega', 'contado', 'cash'].some(k => pm.includes(k));

                                  const realBalance = getOrderAmount(order) - parseFloat(order.total_cash_registered || 0);
                                  // Umbral de ±$2000 para tolerar redondeo de efectivo y pequeñas diferencias (más o menos)
                                  const isBlocked = realBalance > 2000 && !isCredit && !isCashMethod;

                                  // Lógica de prioridad y delay
                                  const todayStr = new Date().toISOString().split('T')[0];
                                  const orderDateRaw = order.delivery_date || order.shipping_date;
                                  let orderDateStr = null;
                                  try { if (orderDateRaw) orderDateStr = new Date(orderDateRaw).toISOString().split('T')[0]; } catch (_) { }

                                  const isFuture = orderDateStr && orderDateStr > todayStr;
                                  let delayDays = 0;
                                  if (orderDateStr && orderDateStr < todayStr) {
                                    try {
                                      const d1 = new Date(todayStr + 'T12:00:00Z');
                                      const d2 = new Date(orderDateStr + 'T12:00:00Z');
                                      delayDays = Math.max(0, Math.floor((d1 - d2) / (1000 * 60 * 60 * 24)));
                                    } catch (_) { }
                                  }

                                  const delayColor = delayDays >= 2 ? 'bg-red-100 text-red-800 border-red-200'
                                    : delayDays === 1 ? 'bg-orange-100 text-orange-800 border-orange-200'
                                      : 'bg-blue-50 text-blue-700 border-blue-100';

                                  return (
                                    <tr key={order.id} className="bg-white hover:bg-blue-50 transition-colors duration-150">
                                      <td className="px-2 py-2 border-r border-gray-200">
                                        <div className="flex flex-col gap-1.5">
                                          <div className="flex items-center gap-2">
                                            <span className="text-base font-semibold text-gray-900">{order.order_number || order.code || order.id}</span>
                                            {isFuture && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black bg-purple-100 text-purple-700 border border-purple-300 animate-pulse uppercase tracking-tight">
                                                <Icons.Calendar className="w-3 h-3 mr-1" />
                                                Programado
                                              </span>
                                            )}
                                            {order.internal_order_numbers && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                                                {order.internal_order_numbers}
                                              </span>
                                            )}
                                          </div>
                                          <div className="max-w-xs">
                                            <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                              {order.customer_name || order.client_name || '-'}
                                              {order.is_modified_after_packing === 1 && (
                                                <Icons.ShieldAlert className="w-4 h-4 text-red-600 animate-pulse" />
                                              )}
                                            </div>
                                            <div className="text-xs text-gray-500 flex items-center gap-1">
                                              <Icons.Phone className="w-3 h-3" />
                                              {order.customer_phone || order.client_phone || ''}
                                              <span className="mx-1">•</span>
                                              <span>{(order.items || []).length} items</span>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-2 py-2 border-r border-gray-200">
                                        <div className="flex flex-col gap-2">
                                          <div className={`text-sm font-medium ${(order.delivery_date || order.shipping_date) ? 'text-gray-700' : 'text-gray-400'}`}>
                                            {(order.delivery_date || order.shipping_date)
                                              ? new Date(order.delivery_date || order.shipping_date).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
                                              : 'PENDIENTE'}
                                          </div>
                                          {delayDays > 0 && (
                                            <div className={`px-2 py-1 rounded-lg text-[10px] font-semibold border uppercase flex items-center gap-1.5 w-fit shadow-sm bg-white ${delayColor}`}>
                                              <Icons.AlertCircle className="w-3.5 h-3.5" />
                                              {delayDays} {delayDays === 1 ? 'día' : 'días'} mora
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-2 py-2 border-r border-gray-200">
                                        <div className="flex flex-col gap-2">
                                          {(() => {
                                            const cName = carriersMap?.[order?.carrier_id]?.name || '';
                                            const channel = getChannelLabel(order, cName);
                                            const badgeClass =
                                              channel === 'Recoge' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
                                                channel === 'Mensajería Local' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                  channel === 'Transportadora' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                                                    'bg-gray-50 text-gray-700 border border-gray-200';
                                            return (
                                              <div className="flex items-center gap-2">
                                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${badgeClass}`}>
                                                  {channel}
                                                </span>
                                                <span className="text-[10px] font-normal text-gray-500 uppercase tracking-tight">
                                                  {getDeliveryMethodLabel(order.delivery_method)}
                                                </span>
                                              </div>
                                            );
                                          })()}
                                          {(() => {
                                            const carrier = carriersMap?.[order?.carrier_id];
                                            if (carrier?.name) return (
                                              <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded-lg border border-gray-100 w-fit">
                                                <Icons.Truck className="w-3.5 h-3.5 text-gray-400" />
                                                {carrier.name}
                                              </div>
                                            );
                                            return null;
                                          })()}
                                          {/* Mensajero Asignado */}
                                          {(() => {
                                            const mName = order?.messenger_name || order?.assigned_messenger_name;
                                            if (!mName) return null;
                                            const statusN = String(order?.messenger_status || '').toLowerCase();
                                            const isAccepted = ['accepted', 'in_delivery', 'en_reparto'].includes(statusN);
                                            return (
                                              <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-lg border w-fit ${isAccepted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                                                <Icons.User className="w-3.5 h-3.5" />
                                                {mName}
                                                {isAccepted && <Icons.Check className="w-3 h-3 text-emerald-600" />}
                                              </div>
                                            );
                                          })()}
                                          {/* Método de Pago */}
                                          {(() => {
                                            const pm = (order?.payment_method || '').toLowerCase();
                                            const pmLabel = pm === 'efectivo' ? 'Efectivo' :
                                              pm === 'transferencia' ? 'Transferencia' :
                                                pm === 'contraentrega' ? 'Contraentrega' :
                                                  pm === 'cliente_credito' || pm === 'credito' ? 'Crédito' :
                                                    pm === 'reposicion' ? 'Reposición' :
                                                      pm || 'N/A';
                                            const pmClass =
                                              pm === 'cliente_credito' || pm === 'credito' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                pm === 'efectivo' || pm === 'contraentrega' ? 'bg-green-100 text-green-700 border-green-200' :
                                                  pm === 'transferencia' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                                    pm === 'reposicion' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                                      'bg-gray-100 text-gray-600 border-gray-200';
                                            return (
                                              <div className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded border w-fit ${pmClass}`}>
                                                <Icons.CreditCard className="w-3 h-3" />
                                                {pmLabel}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </td>
                                      <td className="px-2 py-2">
                                        <div className="flex flex-col gap-1">
                                          {(() => {
                                            const mainAmount = getOrderAmount(order);
                                            const internalAmount = parseFloat(order.internal_orders_total || 0);
                                            const totalWithInternal = mainAmount + internalAmount;

                                            return (
                                              <div className="flex flex-col">
                                                <span className="text-base font-semibold text-gray-900">${totalWithInternal.toLocaleString('es-CO')}</span>
                                                {internalAmount > 0 && (
                                                  <span className="text-[9px] font-normal text-orange-700 bg-orange-50 px-1 rounded w-fit">
                                                    Inc. internos
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          {(() => {
                                            try {
                                              const detail = getBlockDetail(order);
                                              const raw = getRawPaymentMethod(order);
                                              const isCredit = isCreditOrder(order);

                                              if (detail) {
                                                const bgColor = detail.color === 'rose' ? 'bg-rose-600' : 'bg-amber-500';
                                                const textColor = detail.color === 'rose' ? 'text-rose-700' : 'text-amber-700';
                                                const lightBg = detail.color === 'rose' ? 'bg-rose-50' : 'bg-amber-50';
                                                const borderColor = detail.color === 'rose' ? 'border-rose-100' : 'border-amber-100';

                                                return (
                                                  <div className="flex flex-col gap-1.5 mt-1">
                                                    <div className="flex flex-col gap-1">
                                                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-black rounded-lg ${bgColor} text-white uppercase animate-pulse w-fit`}>
                                                        {detail.label}
                                                      </span>
                                                      <div className={`text-[10px] font-black ${textColor} ${lightBg} px-1.5 py-1 rounded border ${borderColor} flex flex-col gap-0.5 shadow-sm`}>
                                                        <span className="flex items-center gap-1">
                                                          <Icons.Info className="w-3 h-3" />
                                                          {detail.subLabel}
                                                        </span>
                                                        <span className="flex items-center gap-1 font-black opacity-80">
                                                          <Icons.DollarSign className="w-3 h-3" />
                                                          Pte: ${detail.balance.toLocaleString('es-CO')}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              }

                                              return (
                                                <div className="flex flex-col gap-1.5 mt-1">
                                                  <div className="flex items-center gap-1.5">
                                                    {isCredit ? (
                                                      <span className="inline-flex px-2 py-0.5 text-[10px] font-black rounded-lg bg-blue-100 text-blue-700 border border-blue-200 uppercase">Credito</span>
                                                    ) : (
                                                      <span className="inline-flex px-2 py-0.5 text-[10px] font-black rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase">Liberado</span>
                                                    )}
                                                    <span className="text-[10px] font-bold text-gray-500 truncate max-w-[80px]">
                                                      {raw || '-'}
                                                    </span>
                                                  </div>
                                                </div>
                                              );
                                            } catch (_) { return null; }
                                          })()}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                        {(() => {
                                          const cNameAction = carriersMap?.[order?.carrier_id]?.name || '';
                                          const channel = getChannelLabel(order, cNameAction);
                                          const isExt = isExternalTruckOrder(order, carriersMap);

                                          const ActionButton = ({ onClick, title, color = 'blue', children, disabled = false, icon: Icon }) => (
                                            <button
                                              onClick={onClick}
                                              disabled={disabled}
                                              className={`inline-flex items-center justify-center w-7 h-7 rounded-lg shadow-sm border transition-all hover:scale-105 active:scale-95 ml-1 ${color === 'green' ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white' :
                                                color === 'indigo' ? 'bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-600 hover:text-white' :
                                                  color === 'rose' ? 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-600 hover:text-white' :
                                                    color === 'purple' ? 'bg-purple-50 text-purple-600 border-purple-100 hover:bg-purple-600 hover:text-white' :
                                                      color === 'blue' ? 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-600 hover:text-white' :
                                                        color === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-500 hover:text-white' :
                                                          'bg-gray-50 text-gray-600 border-gray-100 hover:bg-gray-600 hover:text-white'
                                                } ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none grayscale' : ''}`}
                                              title={title}
                                            >
                                              {Icon && <Icon className="w-3.5 h-3.5" />}
                                              {children && <span className="sr-only">{children}</span>}
                                            </button>
                                          );

                                          if (!!order.is_modified_after_packing) {
                                            const onlyNotes = !hasProductChanges(order);
                                            return (
                                              <div className="flex items-center justify-end">
                                                {onlyNotes ? (
                                                  <button
                                                    onClick={() => {
                                                      setSelectedOrderForChanges(order);
                                                      setShowChangesModal(true);
                                                    }}
                                                    className="inline-flex items-center px-3 py-1.5 text-[10px] font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-lg shadow-emerald-200 border border-emerald-700 uppercase tracking-wider"
                                                    title="Solo cambiaron las notas, ver detalle para validar."
                                                  >
                                                    <Icons.CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                                                    Validar Notas
                                                  </button>
                                                ) : (
                                                  <>
                                                    <button
                                                      onClick={() => handleReturnToPacking(order.id)}
                                                      className="inline-flex items-center px-3 py-1.5 text-[10px] font-black text-white bg-rose-600 hover:bg-rose-700 rounded-lg animate-pulse shadow-lg shadow-rose-200 border border-rose-700 uppercase tracking-wider"
                                                      title="Modificado en SIIGO, debe volver a empaque."
                                                    >
                                                      <Icons.AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                                                      Devolver
                                                    </button>
                                                    <button
                                                      onClick={() => {
                                                        setSelectedOrderForChanges(order);
                                                        setShowChangesModal(true);
                                                      }}
                                                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg shadow-sm border bg-white text-blue-600 border-blue-200 hover:bg-blue-50 ml-1.5 transition-all hover:scale-110 active:scale-95"
                                                      title="Ver detalle de cambios"
                                                    >
                                                      <Icons.Eye className="w-4 h-4" />
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          }

                                          return (
                                            <div className="flex items-center justify-end">
                                              {channel === 'Recoge' && (() => {
                                                const { productDue } = computeCollectionAmounts(order);
                                                const isReleased = order.validation_status === 'approved';

                                                let tooltip = "Marcar como Entregado";
                                                let isDisabled = false;

                                                if (isFuture) {
                                                  tooltip = `Pedido programado para futuro (${sub.dateLabel}). No se puede entregar hoy.`;
                                                  isDisabled = true;
                                                } else if (productDue > 1) {
                                                  tooltip = `Saldo pendiente: $${productDue.toLocaleString()}.`;
                                                  isDisabled = true;
                                                } else if (!isReleased && !isCreditOrder(order)) {
                                                  // RELAXED LOGIC: Permite entregar si el saldo está cubierto
                                                  // Solo activar bloqueo si realmente queremos asegurar validación manual.
                                                  // En este caso, si el saldo es $0, permitimos entregar.
                                                  // tooltip = "Pendiente de liberación por Cartera.";
                                                  // isDisabled = true; // DISABLED BLOCKING
                                                }

                                                return (
                                                  <ActionButton
                                                    color="green"
                                                    icon={Icons.PackageCheck}
                                                    title={tooltip}
                                                    disabled={isDisabled}
                                                    onClick={async () => {
                                                      try {
                                                        const res = await logisticsService.markPickupDelivered({ orderId: order.id, delivery_notes: 'Entregado en bodega' });
                                                        toast.success(res?.message || 'Entregado');
                                                        setOrders(prev => prev.filter(o => o.id !== order.id));
                                                        loadReadyOrders({ silent: true });
                                                      } catch (e) {
                                                        const msg = e?.response?.data?.message || '';
                                                        if (msg.includes('saldo') || msg.includes('pago')) {
                                                          setPaymentModal({ open: true, order, method: String(order?.payment_method || '').toLowerCase().includes('transfer') ? 'transferencia' : 'efectivo', amount: '0', notes: '', file: null });
                                                        } else toast.error(msg || 'Error');
                                                      }
                                                    }}
                                                  />
                                                );
                                              })()}

                                              {channel === 'Mensajería Local' && (() => {
                                                const isAssigned = !!(order?.messenger_name || order?.assigned_messenger_name || order?.assigned_messenger_id);
                                                const currentMessengerId = order?.assigned_messenger_id ? String(order.assigned_messenger_id) : '';

                                                return (
                                                  <ActionButton
                                                    color="indigo"
                                                    icon={isAssigned ? Icons.RefreshCw : Icons.UserPlus}
                                                    title={isFuture ? `Programado para futuro (${sub.dateLabel}). No se puede asignar hoy.` : isAssigned ? `Reasignar mensajero (actual: ${order?.messenger_name || order?.assigned_messenger_name || ''})` : "Asignar mensajero"}
                                                    disabled={isFuture}
                                                    onClick={() => setAssignModal({ open: true, order, messengerId: currentMessengerId })}
                                                  />
                                                );
                                              })()}

                                              {channel === 'Transportadora' && !isExt && (() => {
                                                // Verificar si tiene pago o es crédito
                                                const { productDue } = computeCollectionAmounts(order);
                                                const pm = (order?.payment_method || '').toLowerCase();
                                                const isCredit = pm === 'cliente_credito' || pm === 'credito' || isCreditOrder(order);
                                                const hasPendingPayment = productDue > 1 && !isCredit;
                                                const deliveryDisabled = hasPendingPayment;
                                                const deliveryTooltip = hasPendingPayment
                                                  ? `No se puede entregar: Saldo pendiente de $${productDue.toLocaleString()}`
                                                  : 'Entregar a Transportadora';

                                                return (
                                                  <div className="flex gap-1">
                                                    <ActionButton color="rose" icon={Icons.FileText} title="Imprimir Guía" onClick={() => setBoxCountModal({ open: true, order })} />
                                                    <ActionButton color="blue" icon={Icons.Truck} title="Modificar Envío" onClick={() => setChangeShippingModal({ open: true, order })} />
                                                    <ActionButton
                                                      color="purple"
                                                      icon={Icons.Send}
                                                      title={isFuture ? `Programado para futuro (${sub.dateLabel}). No se puede despachar hoy.` : deliveryTooltip}
                                                      disabled={deliveryDisabled || isFuture}
                                                      onClick={async () => {
                                                        try { await logisticsService.markDeliveredToCarrier({ orderId: order.id, delivery_notes: null }); toast.success('Entregado a transportadora'); loadReadyOrders({ silent: true }); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
                                                      }}
                                                    />
                                                  </div>
                                                );
                                              })()}

                                              {isExt && (() => {
                                                // Verificar si tiene pago o es crédito
                                                const { productDue } = computeCollectionAmounts(order);
                                                const pm = (order?.payment_method || '').toLowerCase();
                                                const isCredit = pm === 'cliente_credito' || pm === 'credito' || isCreditOrder(order);
                                                const hasPendingPayment = productDue > 1 && !isCredit;
                                                const deliveryDisabled = hasPendingPayment;
                                                const deliveryTooltip = hasPendingPayment
                                                  ? `No se puede entregar: Saldo pendiente de $${productDue.toLocaleString()}`
                                                  : 'Entregar a Camión';

                                                return (
                                                  <div className="flex gap-1">
                                                    <ActionButton
                                                      color="rose"
                                                      icon={Icons.FileText}
                                                      title="Generar Guía Camión Externo"
                                                      onClick={async () => {
                                                        setShowSenderForm(false); setShowRecipientForm(false);
                                                        const sD = await systemConfigService.getSenderDefault();
                                                        const s = sD || { name: 'PERLAS EXPLOSIVAS COLOMBIA SAS', nit: '901749888', phone: '3105244298', address: 'Calle 50 # 31-46', city: 'Medellín', department: 'Antioquia', email: 'logistica@perlas-explosivas.com' };
                                                        const p = order.parsed_shipping_data || {};
                                                        const r = { name: p.name || order.customer_name || '', phone: p.phone || order.customer_phone || order.phone || '', address: p.address || order.customer_address || order.address || '', city: p.city || order.customer_city || order.city || '', department: p.department || order.customer_department || order.department || '', nit: p.nit || order.customer_nit || '', email: order.customer_email || order.email || '' };
                                                        setTruckGuideModal(prev => ({ ...prev, open: true, order, plate: '', driver: '', whatsapp: '', boxes: '', notes: '', sender: s, recipient: r, saveSenderDefault: !!sD }));
                                                      }}
                                                    />
                                                    <ActionButton
                                                      color="purple"
                                                      icon={Icons.Truck}
                                                      title={deliveryTooltip}
                                                      disabled={deliveryDisabled}
                                                      onClick={async () => {
                                                        try { await logisticsService.markDeliveredToCarrier({ orderId: order.id, delivery_notes: 'Entregado a camión externo' }); toast.success('Entregado a camión'); loadReadyOrders({ silent: true }); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
                                                      }}
                                                    />
                                                  </div>
                                                );
                                              })()}

                                              {/* Botón Validar Nota - Solo si is_modified_after_packing = 1 */}
                                              {order.is_modified_after_packing === 1 && (
                                                <ActionButton
                                                  color="green"
                                                  icon={Icons.CheckCircle}
                                                  title="Validar Nota"
                                                  onClick={async () => {
                                                    try {
                                                      await handleValidateNoteChange(order.id);
                                                    } catch (e) {
                                                      console.error(e);
                                                    }
                                                  }}
                                                />
                                              )}

                                              {/* Botón Devolver a Empaque - Solo para Admin */}
                                              {isAdmin && (
                                                <ActionButton
                                                  color="gray"
                                                  icon={Icons.CornerUpLeft}
                                                  title="Devolver a Empaque (Solo Admin)"
                                                  onClick={() => setReturnModal({ open: true, order, reason: '' })}
                                                />
                                              )}

                                              {/* Botón Pase Libre (Admin) */}
                                              {isAdmin && (
                                                <ActionButton
                                                  color="amber"
                                                  icon={Icons.Unlock}
                                                  title="Pase Libre (Admin)"
                                                  onClick={() => {
                                                    // Determinar la acción basada en el canal/carrier
                                                    let action = null;
                                                    if (channel === 'Recoge') {
                                                      action = async (startReason) => {
                                                        const res = await logisticsService.markPickupDelivered({
                                                          orderId: order.id,
                                                          delivery_notes: 'Entregado en bodega [ADMIN OVERRIDE]',
                                                          override: true,
                                                          adminNote: startReason
                                                        });
                                                        toast.success('Pase Libre aplicado: ' + (res?.message || 'Entregado'));
                                                        setOrders(prev => prev.filter(o => o.id !== order.id));
                                                        loadReadyOrders({ silent: true });
                                                      };
                                                    } else if (channel === 'Transportadora' || (isExt && channel !== 'Recoge')) {
                                                      action = async (startReason) => {
                                                        await logisticsService.markDeliveredToCarrier({
                                                          orderId: order.id,
                                                          delivery_notes: 'Entregado a transportadora [ADMIN OVERRIDE]',
                                                          override: true,
                                                          adminNote: startReason
                                                        });
                                                        toast.success('Pase Libre aplicado: Entregado a transportadora');
                                                        loadReadyOrders({ silent: true });
                                                      };
                                                    } else if (channel === 'Mensajería Local') {
                                                      // Para mensajería, si hay un mensajero seleccionado en el modal se podría usar, 
                                                      // pero aquí el botón está en la fila. El pase libre forzaría la asignación 
                                                      // saltando validaciones de estado? 
                                                      // Por ahora, para mensajería el flujo suele ser asignar -> mensajero acepta -> iniciar -> completar.
                                                      // El pase libre "Entregar" directo no aplica tanto, salvo si se quiere marcar como completado.
                                                      // Asumimos que el "Pase Libre" es para cerrar el pedido (marcar entregado).
                                                      // OJO: La solicitud dice "dejar pasar al proximo estado".
                                                      // Si está 'listo_para_entrega', el proximo en mensajeria es 'assigned'.
                                                      // PERO si el problema es saldo, el bloqueo suele estar al asignar o al completar.
                                                      // Vamos a asumir que para Mensajería, el Pase Libre abre el modal de Asignar CON bandera.
                                                      toast('Para Mensajería, usa la asignación normal. El Pase Libre está pensado para cerrar entregas bloqueadas por saldo.', { icon: 'ℹ️' });
                                                      return;
                                                    }

                                                    if (action) {
                                                      setFreePassModal({ open: true, order, action });
                                                    }
                                                  }}
                                                />
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            )));

                          return (
                            <React.Fragment key="rows-content">
                              {renderRows(g.subGroups || [])}
                              {(() => {
                                const isLocalGroupMarker =
                                  g.isLocal ||
                                  g.key === 'Mensajería Local' ||
                                  String(g.title || '').toLowerCase().includes('mensajeria local') ||
                                  (g.messengerGroups && g.messengerGroups.length > 0);

                                if (!isLocalGroupMarker) return null;

                                return (g.messengerGroups || []).map((mGroup) => (
                                  <React.Fragment key={mGroup.messengerName}>
                                    {/* Fila de Mensajero */}
                                    <tr className={`${mGroup.isAccepted ? 'bg-emerald-50/50' : 'bg-gray-100/40'}`}>
                                      <td colSpan="5" className={`px-6 py-2 border-l-4 ${mGroup.isAccepted ? 'border-emerald-500' : 'border-gray-400'}`}>
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <div className={`p-1 rounded-lg ${mGroup.isAccepted ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-500'}`}>
                                              <Icons.User className="w-4 h-4" />
                                            </div>
                                            <div className="flex flex-col">
                                              <span className={`text-[12px] font-black uppercase tracking-widest ${mGroup.isAccepted ? 'text-emerald-900' : 'text-gray-600'}`}>
                                                {mGroup.messengerName}
                                                {mGroup.isAccepted && (
                                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-200 text-emerald-800 uppercase shadow-sm border border-emerald-300">
                                                    <Icons.Check className="w-2.5 h-2.5 mr-1" /> ACEPTÓ DESPACHO
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Desglose de Asignación por Mensajero */}
                                          <div className="flex items-center gap-4">
                                            <div className="flex flex-col items-end">
                                              <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Asignados</span>
                                              <span className="text-xs font-black text-gray-700">{mGroup.totalCount}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                              <span className="text-[9px] font-black text-emerald-500 uppercase tracking-tighter">Aprobados</span>
                                              <span className="text-xs font-black text-emerald-700">{mGroup.acceptedCount}</span>
                                            </div>
                                            {mGroup.pendingCount > 0 && (
                                              <div className="flex flex-col items-end">
                                                <span className="text-[9px] font-black text-rose-500 uppercase tracking-tighter">Por Aprobar</span>
                                                <div className="flex items-center gap-1">
                                                  <span className="text-xs font-black text-rose-700">{mGroup.pendingCount}</span>
                                                  <Icons.AlertCircle className="w-3 h-3 text-rose-500 animate-pulse" />
                                                </div>
                                              </div>
                                            )}
                                            {!mGroup.pendingCount && mGroup.totalCount > 0 && (
                                              <div className="bg-emerald-100 text-emerald-700 p-1 rounded-full">
                                                <Icons.Check className="w-3 h-3" />
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                    {renderRows(mGroup.subGroups, { name: mGroup.messengerName })}
                                  </React.Fragment>
                                ));
                              })()}
                            </React.Fragment>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      {/* Modal Registrar Pago en Bodega */}
      {paymentModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h3 className="text-lg font-semibold mb-2">Registrar pago en bodega</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Método de pago</label>
                <select
                  value={paymentModal.method}
                  onChange={(e) => setPaymentModal(p => ({ ...p, method: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Monto</label>
                <input
                  type="number"
                  value={paymentModal.amount}
                  onChange={(e) => setPaymentModal(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notas (opcional)</label>
                <textarea
                  value={paymentModal.notes}
                  onChange={(e) => setPaymentModal(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              {paymentModal.method === 'transferencia' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Comprobante (foto)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPaymentModal(p => ({ ...p, file: e.target.files?.[0] || null }))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Obligatorio para transferencia.</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setPaymentModal({ open: false, order: null, method: 'efectivo', amount: '', notes: '', file: null })}
                className="px-3 py-1 rounded border"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    await logisticsService.receivePickupPayment({
                      orderId: paymentModal.order.id,
                      payment_method: paymentModal.method,
                      amount: Number(paymentModal.amount) || 0,
                      notes: paymentModal.notes,
                      file: paymentModal.file
                    });
                    // Caso de uso: después de registrar el pago, el pedido debe permanecer en la vista
                    // y solo salir cuando se marque como ENTREGADO.
                    toast.success('Pago registrado');
                    // Actualización optimista: marcar pago en el estado local sin sacar el pedido de la lista
                    setOrders(prev => prev.map(o => {
                      if (Number(o.id) !== Number(paymentModal.order.id)) return o;
                      const count = Number(o.cash_register_count || 0);
                      return { ...o, cash_register_count: count + 1, has_payment: 1 };
                    }));
                    setPaymentModal({ open: false, order: null, method: 'efectivo', amount: '', notes: '', file: null });
                    // Refrescar para mantener consistencia sin cambiar el bucket (silent para no perder scroll)
                    loadReadyOrders({ silent: true });
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'No se pudo registrar el pago');
                  }
                }}
                className="px-3 py-1 rounded bg-blue-600 text-white"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Asignar Mensajero */}
      {assignModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h3 className="text-lg font-semibold mb-2">Asignar mensajero</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Mensajero</label>
                <select
                  value={assignModal.messengerId}
                  onChange={(e) => setAssignModal(p => ({ ...p, messengerId: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value="">Selecciona un mensajero</option>
                  {(messengers || []).map(m => (
                    <option key={m.id} value={m.id}>{m.full_name || m.username || `ID ${m.id}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setAssignModal({ open: false, order: null, messengerId: '' })}
                className="px-3 py-1 rounded border"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!assignModal.messengerId) {
                    toast.error('Selecciona un mensajero');
                    return;
                  }
                  try {
                    const res = await logisticsService.assignMessenger({
                      orderId: assignModal.order.id,
                      messengerId: Number(assignModal.messengerId)
                    });
                    toast.success(res?.message || 'Mensajero asignado');
                    // Optimista: actualizar datos del pedido y mantenerlo visible
                    setOrders(prev => {
                      const idNum = Number(assignModal.order.id);
                      const messengerNum = Number(assignModal.messengerId);
                      const selected = (messengers || []).find(m => Number(m.id) === messengerNum);
                      const displayName = selected?.full_name || selected?.username || `ID ${messengerNum}`;
                      return prev.map(o => {
                        if (Number(o.id) !== idNum) return o;
                        return {
                          ...o,
                          assigned_messenger_id: messengerNum,
                          assigned_messenger: String(messengerNum),
                          messenger_status: 'assigned',
                          messenger_name: displayName,
                          assigned_messenger_name: displayName
                        };
                      });
                    });
                    setAssignModal({ open: false, order: null, messengerId: '' });
                    // Refrescar para asegurar consistencia con backend (silent para no perder scroll)
                    loadReadyOrders({ silent: true });
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'No se pudo asignar el mensajero');
                  }
                }}
                className="px-3 py-1 rounded bg-indigo-600 text-white"
              >
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Guía Camión Externo */}
      {truckGuideModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6">
            <h3 className="text-lg font-semibold mb-4 border-b pb-2">Guía - Camión Externo</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Columna Izquierda: Datos de Transporte y Remitente */}
              <div className="space-y-4">
                {/* Datos de transporte */}
                <div className="border rounded p-3 bg-white">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-semibold text-indigo-700">Datos de transporte</h4>
                    <select
                      className="text-xs border rounded px-2 py-1 max-w-[200px]"
                      onChange={(e) => {
                        const d = externalDrivers.find(ed => String(ed.id) === e.target.value);
                        if (d) {
                          setTruckGuideModal(p => ({
                            ...p,
                            driver: d.name,
                            plate: d.plate || '',
                            whatsapp: d.phone || ''
                          }));
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="" disabled>Cargar conductor...</option>
                      {Object.entries(
                        externalDrivers.reduce((acc, d) => {
                          const city = d.city ? d.city.toUpperCase() : 'GENERAL';
                          if (!acc[city]) acc[city] = [];
                          acc[city].push(d);
                          return acc;
                        }, {})
                      ).sort((a, b) => a[0].localeCompare(b[0])).map(([city, drivers]) => (
                        <optgroup key={city} label={city}>
                          {drivers.map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Placa del vehículo</label>
                      <input
                        type="text"
                        value={truckGuideModal.plate}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, plate: e.target.value }))}
                        className="w-full border px-3 py-2 rounded"
                        placeholder="ABC123"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Nombre del conductor</label>
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={truckGuideModal.driver}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, driver: e.target.value }))}
                          className="w-full border px-3 py-2 rounded"
                          placeholder="Nombre y Apellido"
                        />
                        {truckGuideModal.driver && !externalDrivers.some(d => d.name.toLowerCase() === truckGuideModal.driver.toLowerCase()) && (
                          <button
                            onClick={async () => {
                              try {
                                await logisticsService.createExternalDriver({
                                  name: truckGuideModal.driver,
                                  plate: truckGuideModal.plate,
                                  phone: truckGuideModal.whatsapp,
                                  city: truckGuideModal.recipient.city
                                });
                                toast.success('Conductor guardado');
                                loadExternalDrivers();
                              } catch (e) {
                                toast.error('Error guardando conductor');
                              }
                            }}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs"
                            title="Guardar nuevo conductor"
                          >
                            <Icons.Save className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">WhatsApp</label>
                      <input
                        type="text"
                        value={truckGuideModal.whatsapp}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, whatsapp: e.target.value }))}
                        className="w-full border px-3 py-2 rounded"
                        placeholder="+57 300..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Cajas</label>
                      <input
                        type="number"
                        min="0"
                        value={truckGuideModal.boxes}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, boxes: e.target.value }))}
                        className="w-full border px-3 py-2 rounded"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Forma de Pago Flete</label>
                      <select
                        value={truckGuideModal.shippingPaymentMethod || ''}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, shippingPaymentMethod: e.target.value }))}
                        className="w-full border px-3 py-2 rounded text-xs"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="CONTADO">Contado (Pago en Origen)</option>
                        <option value="CONTRA ENTREGA">Contra Entrega (Pago en Destino)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium mb-1">Notas (opcional)</label>
                    <textarea
                      value={truckGuideModal.notes}
                      onChange={(e) => setTruckGuideModal(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border px-3 py-2 rounded h-20"
                      placeholder="Indicaciones adicionales..."
                    />
                  </div>
                </div>

                {/* Remitente */}
                <div className="border rounded p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Remitente</h4>
                    <div className="text-right">
                      <label className="text-xs inline-flex items-center mr-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={truckGuideModal.saveSenderDefault}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, saveSenderDefault: e.target.checked }))}
                          className="mr-1"
                        />
                        Guardar default
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowSenderForm(s => !s)}
                        className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-100"
                      >
                        {showSenderForm ? 'Ocultar' : 'Editar'}
                      </button>
                    </div>
                  </div>

                  {!showSenderForm ? (
                    <div className="text-xs text-gray-600">
                      <p className="font-medium">{truckGuideModal.sender?.name || 'Sin empresa'}</p>
                      <p>{truckGuideModal.sender?.city || '-'}, {truckGuideModal.sender?.department || '-'}</p>
                      <p>{truckGuideModal.sender?.address}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="col-span-2">
                        <label className="block text-[10px] font-medium text-gray-500">Empresa</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.name}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, name: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500">NIT</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.nit}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, nit: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500">Teléfono</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.phone}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, phone: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-medium text-gray-500">Dirección</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.address}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, address: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500">Ciudad</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.city}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, city: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500">Depto</label>
                        <input
                          type="text"
                          value={truckGuideModal.sender.department}
                          onChange={(e) => setTruckGuideModal(p => ({ ...p, sender: { ...p.sender, department: e.target.value } }))}
                          className="w-full border px-2 py-1 rounded text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Columna Derecha: Receptor */}
              <div className="border rounded p-4 bg-gray-50 h-full">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">Receptor</h4>
                  <button
                    type="button"
                    onClick={() => setShowRecipientForm(s => !s)}
                    className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-100"
                  >
                    {showRecipientForm ? 'Ocultar' : 'Editar'}
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Nombre Completo</label>
                    <input
                      type="text"
                      value={truckGuideModal.recipient.name}
                      onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, name: e.target.value } }))}
                      className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">NIT / CC</label>
                      <input
                        type="text"
                        value={truckGuideModal.recipient.nit}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, nit: e.target.value } }))}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">Teléfono</label>
                      <input
                        type="text"
                        value={truckGuideModal.recipient.phone}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, phone: e.target.value } }))}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Dirección de Entrega</label>
                    <input
                      type="text"
                      value={truckGuideModal.recipient.address}
                      onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, address: e.target.value } }))}
                      className="w-full border px-3 py-2 rounded"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">Ciudad</label>
                      <input
                        type="text"
                        value={truckGuideModal.recipient.city}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, city: e.target.value } }))}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">Departamento</label>
                      <input
                        type="text"
                        value={truckGuideModal.recipient.department}
                        onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, department: e.target.value } }))}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600">Email</label>
                    <input
                      type="text"
                      value={truckGuideModal.recipient.email}
                      onChange={(e) => setTruckGuideModal(p => ({ ...p, recipient: { ...p.recipient, email: e.target.value } }))}
                      className="w-full border px-3 py-2 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setTruckGuideModal({ open: false, order: null, plate: '', driver: '', whatsapp: '', boxes: '', notes: '', shippingPaymentMethod: '' })}
                className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    // Validaciones
                    if (!truckGuideModal.shippingPaymentMethod) {
                      toast.error('Debe seleccionar la Forma de Pago Flete');
                      return;
                    }
                    if (!truckGuideModal.boxes || Number(truckGuideModal.boxes) <= 0) {
                      toast.error('Debe ingresar el número de cajas');
                      return;
                    }

                    // Guardar remitente por defecto si aplica
                    if (truckGuideModal.saveSenderDefault) {
                      try {
                        await systemConfigService.saveSenderDefault(truckGuideModal.sender);
                      } catch (e) {
                        console.warn('No se pudo guardar remitente por defecto:', e?.message);
                      }
                    }
                    const extra = [
                      truckGuideModal.notes ? `Notas: ${truckGuideModal.notes}` : null
                    ].filter(Boolean).join(' | ');

                    const payload = {
                      orderId: truckGuideModal.order.id,
                      shippingMethod: 'camion_externo',
                      transportCompany: 'Camión Externo',
                      // Nuevos objetos
                      sender: { ...truckGuideModal.sender },
                      recipient: {
                        ...truckGuideModal.recipient,
                        paymentMethod: truckGuideModal.shippingPaymentMethod // Enviar selección explícita
                      },
                      driver: {
                        plate: truckGuideModal.plate,
                        name: truckGuideModal.driver,
                        whatsapp: truckGuideModal.whatsapp,
                        boxes: truckGuideModal.boxes
                      },
                      // Notas libres
                      notes: extra
                    };
                    const resp = await logisticsService.generateGuide(payload);
                    // Descargar blob
                    const blob = new Blob([resp.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `guia-envio-${truckGuideModal.order.order_number}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Guía generada');
                    setTruckGuideModal({
                      open: false, order: null,
                      plate: '', driver: '', whatsapp: '', boxes: '', notes: '', shippingPaymentMethod: '',
                      sender: {
                        name: '', nit: '', phone: '', address: '', city: '', department: '', email: ''
                      },
                      recipient: {
                        name: '', phone: '', address: '', city: '', department: '', nit: '', email: ''
                      },
                      saveSenderDefault: false
                    });
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'No se pudo generar la guía');
                  }
                }}
                className="px-4 py-2 rounded bg-rose-600 text-white hover:bg-rose-700 shadow-sm"
              >
                Generar Guía
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Pase Libre */}
      <FreePassModal
        open={freePassModal.open}
        order={freePassModal.order}
        onClose={() => setFreePassModal({ open: false, order: null, action: null })}
        onConfirm={freePassModal.action}
      />

      {/* Modal Devolver a Empaque */}
      {returnModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h3 className="text-lg font-semibold mb-2">Devolver a Empaque</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Motivo</label>
                <textarea
                  value={returnModal.reason}
                  onChange={(e) => setReturnModal(p => ({ ...p, reason: e.target.value }))}
                  className="w-full border px-3 py-2 rounded"
                  placeholder="Ej: Cliente solicita adicionar producto"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setReturnModal({ open: false, order: null, reason: '' })}
                className="px-3 py-1 rounded border"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    await logisticsService.returnToPackaging({
                      orderId: returnModal.order.id,
                      reason: returnModal.reason
                    });
                    toast.success('Pedido devuelto a Empaque');
                    setOrders(prev => prev.filter(o => Number(o.id) !== Number(returnModal.order.id)));
                    setReturnModal({ open: false, order: null, reason: '' });
                    loadReadyOrders({ silent: true });
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'No se pudo devolver a Empaque');
                  }
                }}
                className="px-3 py-1 rounded bg-gray-700 text-white"
              >
                Devolver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cambiar Información de Envío */}
      {changeShippingModal.open && (
        <ChangeShippingModal
          order={changeShippingModal.order}
          onClose={() => setChangeShippingModal({ open: false, order: null })}
          onSuccess={({ orderId, newCarrierId, generateGuide }) => {
            try {
              setOrders(prev => prev.map(o => {
                if (Number(o.id) !== Number(orderId)) return o;
                return { ...o, carrier_id: newCarrierId, shipping_guide_generated: 0, shipping_guide_path: null };
              }));
            } catch { }
            loadReadyOrders();
            if (generateGuide) {
              // Trigger the box count modal for the updated order
              const updatedOrder = orders.find(o => Number(o.id) === Number(orderId));
              if (updatedOrder) {
                setBoxCountModal({ open: true, order: { ...updatedOrder, carrier_id: newCarrierId } });
              } else {
                // Fallback if not found in current state (though it should be)
                loadReadyOrders().then(() => {
                  setBoxCountModal({ open: true, order: { id: orderId, carrier_id: newCarrierId } });
                });
              }
            }
          }}
        />
      )}
      {/* Modal Subir Comprobante */}
      <UploadEvidenceModal
        isOpen={uploadEvidenceModal.open}
        onClose={() => setUploadEvidenceModal({ open: false, order: null, file: null })}
        order={uploadEvidenceModal.order}
        onSuccess={() => {
          loadReadyOrders();
          setUploadEvidenceModal({ open: false, order: null, file: null });
        }}
      />
      <OrderChangesModal
        isOpen={showChangesModal}
        onClose={() => {
          setShowChangesModal(false);
          setSelectedOrderForChanges(null);
        }}
        order={selectedOrderForChanges}
        onValidateNotes={handleValidateNoteChange}
      />
      {/* Modal Acta de Despacho a Transportadora */}
      <CarrierDispatchModal
        open={dispatchModal.open}
        orders={dispatchModal.orders}
        carrierName={dispatchModal.carrierName}
        carrierId={dispatchModal.carrierId}
        onClose={() => setDispatchModal({ open: false, orders: [], carrierName: '', carrierId: null })}
        onSuccess={() => loadReadyOrders()}
      />
    </div>
  );
}
